import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { 
  Shield, FileText, Users, Calendar, Settings, Eye,
  Plus, ArrowLeft, Clock, CheckCircle, LogOut, UserPlus,
  Loader2, AlertCircle, Mail, Copy, Trash2, Ban,
  KeyRound, ShieldCheck, ShieldOff, RefreshCw, Smartphone,
  Lock, History, Filter, ChevronLeft, ChevronRight, Bot
} from 'lucide-react';
import CrawlerSettingsPanel from '../components/CrawlerSettingsPanel';

const AdminPanel = () => {
  const { 
    admin, isAuthenticated, isSuperAdmin, logout,
    listAdmins, listInvites, createInvite, isLoading,
    get2FAStatus, setup2FA, verifySetup2FA, disable2FA, regenerateBackupCodes,
    changePassword, getAuditLogs, getAuditLogActions
  } = useAuth();
  const { token } = useAuth();
  
  const [activeTab, setActiveTab] = useState('articles');
  
  // Admin management state
  const [admins, setAdmins] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'EDITOR',
    first_name: '',
    last_name: ''
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [newInviteLink, setNewInviteLink] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  
  // 2FA state
  const [twoFAStatus, setTwoFAStatus] = useState(null);
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [twoFASetupData, setTwoFASetupData] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAError, setTwoFAError] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [showRegenerateBackup, setShowRegenerateBackup] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');
  
  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  
  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditActions, setAuditActions] = useState([]);
  const [auditFilter, setAuditFilter] = useState('');
  const [auditEmailFilter, setAuditEmailFilter] = useState('');

  // Article data (still mock for now - will be replaced with API in Phase 2)
  const articles = [
    { id: 1, slug: 'the-fluency-gap-when-competence-gets-misread', title: 'The Fluency Gap: When Competence Gets Misread', publishDate: '2026-02-23', status: 'scheduled' },
    { id: 2, slug: 'the-unspoken-code-what-people-mean-when-they-do-not-say-it', title: 'The Unspoken Code: What People Do Not Say It', publishDate: '2026-03-09', status: 'scheduled' },
    { id: 3, slug: 'adaptation-is-not-surrender', title: 'Adaptation Is Not Surrender', publishDate: '2026-03-23', status: 'scheduled' },
    { id: 4, slug: 'the-cultural-fluency-matrix-a-mirror-not-a-label', title: 'The Cultural Fluency Matrix: A Mirror, Not a Label', publishDate: '2026-04-06', status: 'scheduled' },
    { id: 5, slug: 'when-accuracy-costs-influence', title: 'When Accuracy Costs Influence', publishDate: '2026-04-20', status: 'scheduled' },
  ];

  // Load admin data
  const loadAdminData = async () => {
    setLoadingAdmins(true);
    
    const [adminsResult, invitesResult] = await Promise.all([
      listAdmins(),
      listInvites()
    ]);
    
    if (adminsResult.success) {
      setAdmins(adminsResult.admins || []);
    }
    if (invitesResult.success) {
      setInvites(invitesResult.invites || []);
    }
    
    setLoadingAdmins(false);
  };
  
  // Load 2FA status
  const load2FAStatus = async () => {
    setTwoFALoading(true);
    const status = await get2FAStatus();
    setTwoFAStatus(status);
    setTwoFALoading(false);
  };
  
  // Load audit logs
  const loadAuditLogs = async (page = 1) => {
    setAuditLoading(true);
    const result = await getAuditLogs(page, 25, auditFilter || null, auditEmailFilter || null);
    if (result) {
      setAuditLogs(result.logs || []);
      setAuditPage(result.page);
      setAuditTotalPages(result.total_pages);
      setAuditTotal(result.total);
    }
    setAuditLoading(false);
  };
  
  // Load audit log action types
  const loadAuditActions = async () => {
    const actions = await getAuditLogActions();
    setAuditActions(actions || []);
  };

  // Load admins and invites when tab changes
  useEffect(() => {
    if (activeTab === 'users' && isSuperAdmin()) {
      loadAdminData();
    }
    if (activeTab === 'settings') {
      load2FAStatus();
    }
    if (activeTab === 'audit') {
      loadAuditLogs(1);
      loadAuditActions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  
  // Check if password change is required
  useEffect(() => {
    if (admin?.must_change_password) {
      setShowPasswordChange(true);
      setActiveTab('settings');
    }
  }, [admin]);
  
  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess(false);
    
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError('New passwords do not match');
      setPasswordLoading(false);
      return;
    }
    
    const result = await changePassword(passwordForm.current, passwordForm.new, passwordForm.confirm);
    if (result.success) {
      setPasswordSuccess(true);
      setPasswordForm({ current: '', new: '', confirm: '' });
      setShowPasswordChange(false);
      setTimeout(() => setPasswordSuccess(false), 3000);
    } else {
      setPasswordError(result.error);
    }
    setPasswordLoading(false);
  };
  
  // Start 2FA setup
  const handleStart2FASetup = async () => {
    setTwoFALoading(true);
    setTwoFAError('');
    const result = await setup2FA();
    if (result.success) {
      setTwoFASetupData(result);
    } else {
      setTwoFAError(result.error);
    }
    setTwoFALoading(false);
  };
  
  // Complete 2FA setup
  const handleVerify2FASetup = async (e) => {
    e.preventDefault();
    setTwoFALoading(true);
    setTwoFAError('');
    const result = await verifySetup2FA(twoFACode);
    if (result.success) {
      setBackupCodes(result.backup_codes);
      setTwoFASetupData(null);
      setTwoFACode('');
      load2FAStatus();
    } else {
      setTwoFAError(result.error);
    }
    setTwoFALoading(false);
  };
  
  // Cancel 2FA setup
  const handleCancel2FASetup = () => {
    setTwoFASetupData(null);
    setTwoFACode('');
    setTwoFAError('');
  };
  
  // Disable 2FA
  const handleDisable2FA = async (e) => {
    e.preventDefault();
    setTwoFALoading(true);
    setTwoFAError('');
    const result = await disable2FA(disableCode);
    if (result.success) {
      setShowDisable2FA(false);
      setDisableCode('');
      load2FAStatus();
    } else {
      setTwoFAError(result.error);
    }
    setTwoFALoading(false);
  };
  
  // Regenerate backup codes
  const handleRegenerateBackupCodes = async (e) => {
    e.preventDefault();
    setTwoFALoading(true);
    setTwoFAError('');
    const result = await regenerateBackupCodes(regenerateCode);
    if (result.success) {
      setBackupCodes(result.backup_codes);
      setShowRegenerateBackup(false);
      setRegenerateCode('');
      load2FAStatus();
    } else {
      setTwoFAError(result.error);
    }
    setTwoFALoading(false);
  };
  
  // Copy backup codes to clipboard
  const copyBackupCodes = async () => {
    if (backupCodes) {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteLoading(true);
    
    const result = await createInvite(inviteForm);
    
    if (result.success) {
      setNewInviteLink(result.invite.invite_link || `${window.location.origin}/admin/accept-invite?token=${result.invite.id}`);
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'EDITOR', first_name: '', last_name: '' });
      loadAdminData();
    } else {
      setInviteError(result.error);
    }
    
    setInviteLoading(false);
  };

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(newInviteLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-navy animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated()) {
    return <Navigate to="/admin/login" replace />;
  }

  const tabs = [
    { id: 'articles', label: 'Articles', icon: FileText },
    { id: 'users', label: 'Users & Access', icon: Users, superAdminOnly: true },
    { id: 'crawlers', label: 'SEO & Crawlers', icon: Bot, superAdminOnly: true },
    { id: 'audit', label: 'Audit Logs', icon: History },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const filteredTabs = tabs.filter(tab => !tab.superAdminOnly || isSuperAdmin());

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
                <ArrowLeft size={18} />
                Back to Site
              </Link>
              <div className="h-6 w-px bg-slate-600" />
              <div className="flex items-center gap-2">
                <Shield size={20} className="text-gold" />
                <h1 className="font-bold text-lg">Admin Panel</h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-slate-300">
                <span className="text-white font-medium">{admin?.first_name} {admin?.last_name}</span>
                <span className="text-slate-400 ml-2">({admin?.role})</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-lg hover:bg-white/20 transition-colors text-sm"
                data-testid="admin-logout-btn"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          {filteredTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-navy text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'articles' && (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-navy">Article Management</h2>
                  <p className="text-slate-500 text-sm mt-1">Manage scheduled and published articles</p>
                </div>
                <button className="flex items-center gap-2 bg-gold text-navy px-4 py-2 rounded-lg font-medium hover:bg-gold/90 transition-colors">
                  <Plus size={18} />
                  New Article
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Article</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Publish Date</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {articles.map((article) => (
                    <tr key={article.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <Link 
                          to={`/articles/${article.slug}`}
                          className="font-medium text-navy hover:text-gold transition-colors"
                        >
                          {article.title}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar size={14} />
                          {new Date(article.publishDate).toLocaleDateString('en-GB', { 
                            day: 'numeric', month: 'short', year: 'numeric' 
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                          new Date() >= new Date(article.publishDate)
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {new Date() >= new Date(article.publishDate) ? (
                            <>
                              <CheckCircle size={12} />
                              Published
                            </>
                          ) : (
                            <>
                              <Clock size={12} />
                              Scheduled
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/articles/${article.slug}`}
                            className="p-2 text-slate-400 hover:text-navy hover:bg-slate-100 rounded-lg transition-colors"
                            title="Preview"
                          >
                            <Eye size={16} />
                          </Link>
                          <button 
                            className="p-2 text-slate-400 hover:text-navy hover:bg-slate-100 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Settings size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Coming Soon Notice */}
            <div className="p-6 border-t border-slate-200 bg-amber-50">
              <p className="text-amber-800 text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                Full article CMS with rich text editor coming in Phase 2
              </p>
            </div>
          </div>
        )}

        {activeTab === 'users' && isSuperAdmin() && (
          <div className="space-y-6">
            {/* New Invite Link Banner */}
            {newInviteLink && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Invite created successfully!</p>
                      <p className="text-sm text-green-600">Share this link with the new admin:</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setNewInviteLink('')}
                    className="text-green-600 hover:text-green-800"
                  >
                    ×
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="text"
                    value={newInviteLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-green-200 rounded-lg text-sm text-slate-700"
                  />
                  <button
                    onClick={copyInviteLink}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Copy size={16} />
                    {copiedLink ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            )}

            {/* Admin Users */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-navy">Admin Users</h2>
                  <p className="text-sm text-slate-500">Manage admin access and roles</p>
                </div>
                <button 
                  onClick={() => setShowInviteModal(true)}
                  className="flex items-center gap-2 bg-gold text-navy px-4 py-2 rounded-lg font-medium hover:bg-gold/90 transition-colors"
                  data-testid="invite-admin-btn"
                >
                  <UserPlus size={18} />
                  Invite Admin
                </button>
              </div>

              {loadingAdmins ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-navy animate-spin" />
                </div>
              ) : (
                <div className="space-y-2">
                  {admins.map((adminUser) => (
                    <div key={adminUser.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          adminUser.role === 'SUPER_ADMIN' ? 'bg-gold/20' : 'bg-navy/10'
                        }`}>
                          <Shield size={18} className={adminUser.role === 'SUPER_ADMIN' ? 'text-gold' : 'text-navy'} />
                        </div>
                        <div>
                          <p className="font-medium text-navy">
                            {adminUser.first_name} {adminUser.last_name}
                          </p>
                          <p className="text-sm text-slate-500">{adminUser.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          adminUser.role === 'SUPER_ADMIN' 
                            ? 'bg-gold/20 text-gold' 
                            : adminUser.role === 'ADMIN'
                              ? 'bg-navy/20 text-navy'
                              : 'bg-slate-200 text-slate-600'
                        }`}>
                          {adminUser.role}
                        </span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          adminUser.status === 'ACTIVE' 
                            ? 'bg-green-100 text-green-700'
                            : adminUser.status === 'PENDING'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                        }`}>
                          {adminUser.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Invites */}
            {invites.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-navy mb-4">Pending Invites</h2>
                <div className="space-y-2">
                  {invites.map((invite) => (
                    <div key={invite.id} className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Mail size={18} className="text-amber-600" />
                        <div>
                          <p className="font-medium text-navy">{invite.email}</p>
                          <p className="text-sm text-slate-500">
                            Expires: {new Date(invite.expires_at).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-200 text-amber-700">
                        {invite.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-navy">Audit Logs</h2>
                <p className="text-sm text-slate-500">Security and activity history</p>
              </div>
              <div className="text-sm text-slate-500">
                {auditTotal} total entries
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-slate-400" />
                <select
                  value={auditFilter}
                  onChange={(e) => { setAuditFilter(e.target.value); loadAuditLogs(1); }}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold"
                  data-testid="audit-filter-action"
                >
                  <option value="">All Actions</option>
                  {auditActions.map(action => (
                    <option key={action} value={action}>{action.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              {isSuperAdmin() && (
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-slate-400" />
                  <input
                    type="email"
                    value={auditEmailFilter}
                    onChange={(e) => setAuditEmailFilter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadAuditLogs(1)}
                    placeholder="Filter by email..."
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold w-48"
                    data-testid="audit-filter-email"
                  />
                  <button
                    onClick={() => loadAuditLogs(1)}
                    className="px-3 py-2 bg-navy text-white rounded-lg text-sm hover:bg-navy/90"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
            
            {auditLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-navy animate-spin" />
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <History size={48} className="mx-auto mb-4 opacity-30" />
                <p>No audit logs found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Timestamp</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Action</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Admin</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Details</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((log, index) => (
                        <tr key={log.id || index} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 text-slate-600 whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleString('en-GB', { 
                              day: '2-digit', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.action?.includes('FAILED') ? 'bg-red-100 text-red-700' :
                              log.action?.includes('LOGIN') ? 'bg-green-100 text-green-700' :
                              log.action?.includes('TWO_FACTOR') ? 'bg-purple-100 text-purple-700' :
                              log.action?.includes('PASSWORD') ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {log.action?.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-slate-600">{log.admin_email}</td>
                          <td className="py-3 px-4 text-slate-500 text-xs max-w-xs truncate">
                            {log.details ? JSON.stringify(log.details) : '-'}
                          </td>
                          <td className="py-3 px-4 text-slate-400 text-xs font-mono">
                            {log.ip_address || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500">
                    Page {auditPage} of {auditTotalPages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => loadAuditLogs(auditPage - 1)}
                      disabled={auditPage <= 1}
                      className="flex items-center gap-1 px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </button>
                    <button
                      onClick={() => loadAuditLogs(auditPage + 1)}
                      disabled={auditPage >= auditTotalPages}
                      className="flex items-center gap-1 px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'calendar' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-navy mb-4">Content Calendar</h2>
            <p className="text-sm text-slate-500 mb-6">Article publishing schedule</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((article) => {
                const date = new Date(article.publishDate);
                const isPast = new Date() >= date;
                return (
                  <div 
                    key={article.id}
                    className={`p-4 rounded-lg border-2 ${
                      isPast ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={16} className={isPast ? 'text-green-600' : 'text-amber-600'} />
                      <span className={`text-sm font-semibold ${isPast ? 'text-green-700' : 'text-amber-700'}`}>
                        {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-navy font-medium text-sm line-clamp-2">{article.title}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'crawlers' && (
          <CrawlerSettingsPanel token={token} />
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Password Change Required Banner */}
            {admin?.must_change_password && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-amber-800">Password Change Required</p>
                  <p className="text-sm text-amber-700">Please change your password before continuing.</p>
                </div>
              </div>
            )}
            
            {/* Account Information */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-navy mb-4">Account Information</h2>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Email:</span> {admin?.email}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  <span className="font-medium">Role:</span> {admin?.role}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  <span className="font-medium">Last Login:</span> {admin?.last_login ? new Date(admin.last_login).toLocaleString('en-GB') : 'N/A'}
                </p>
              </div>
            </div>
            
            {/* Password Management */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-navy/10 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-navy" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-navy">Password</h2>
                  <p className="text-sm text-slate-500">Change your account password</p>
                </div>
              </div>
              
              {passwordSuccess && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm mb-4">
                  <CheckCircle size={16} />
                  Password changed successfully
                </div>
              )}
              
              {passwordError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">
                  <AlertCircle size={16} />
                  {passwordError}
                </div>
              )}
              
              {showPasswordChange || admin?.must_change_password ? (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
                    <input
                      type="password"
                      value={passwordForm.current}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-gold/50 focus:border-gold"
                      required
                      data-testid="password-current"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                    <input
                      type="password"
                      value={passwordForm.new}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-gold/50 focus:border-gold"
                      required
                      data-testid="password-new"
                    />
                    <p className="text-xs text-slate-500 mt-1">Minimum 8 characters with uppercase, lowercase, and numbers</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordForm.confirm}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-gold/50 focus:border-gold"
                      required
                      data-testid="password-confirm"
                    />
                  </div>
                  <div className="flex gap-3">
                    {!admin?.must_change_password && (
                      <button
                        type="button"
                        onClick={() => { setShowPasswordChange(false); setPasswordForm({ current: '', new: '', confirm: '' }); setPasswordError(''); }}
                        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={passwordLoading}
                      className="flex-1 px-4 py-2 bg-gold text-navy font-medium rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="password-submit"
                    >
                      {passwordLoading ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Changing...
                        </>
                      ) : (
                        'Change Password'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  onClick={() => setShowPasswordChange(true)}
                  className="flex items-center gap-2 px-4 py-2 text-navy border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid="password-change-btn"
                >
                  <Lock size={16} />
                  Change Password
                </button>
              )}
            </div>
            
            {/* Two-Factor Authentication */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-navy/10 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-navy" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-navy">Two-Factor Authentication</h2>
                  <p className="text-sm text-slate-500">Add an extra layer of security to your account</p>
                </div>
              </div>
              
              {twoFALoading && !twoFASetupData && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-navy animate-spin" />
                </div>
              )}
              
              {/* Error Display */}
              {twoFAError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">
                  <AlertCircle size={16} />
                  {twoFAError}
                </div>
              )}
              
              {/* Backup Codes Display */}
              {backupCodes && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="w-5 h-5 text-amber-600" />
                    <h3 className="font-semibold text-amber-800">Save Your Backup Codes</h3>
                  </div>
                  <p className="text-sm text-amber-700 mb-3">
                    Store these codes in a safe place. Each code can only be used once.
                  </p>
                  <div className="bg-white rounded-lg p-4 font-mono text-sm grid grid-cols-2 gap-2">
                    {backupCodes.map((code, index) => (
                      <span key={index} className="text-slate-700">{code}</span>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={copyBackupCodes}
                      className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 transition-colors"
                    >
                      <Copy size={14} />
                      Copy Codes
                    </button>
                    <button
                      onClick={() => setBackupCodes(null)}
                      className="px-3 py-1.5 text-amber-700 hover:text-amber-800 text-sm"
                    >
                      I&apos;ve saved them
                    </button>
                  </div>
                </div>
              )}
              
              {/* 2FA Setup Flow */}
              {twoFASetupData ? (
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Smartphone className="w-5 h-5 text-navy" />
                      <h3 className="font-semibold text-navy">Scan QR Code</h3>
                    </div>
                    <p className="text-sm text-slate-600 mb-4">
                      Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                    </p>
                    <div className="flex justify-center mb-4">
                      <img 
                        src={twoFASetupData.qr_code} 
                        alt="2FA QR Code" 
                        className="w-48 h-48 border-4 border-white shadow-lg rounded-lg"
                        data-testid="2fa-qr-code"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 mb-1">Or enter this code manually:</p>
                      <code className="text-sm font-mono bg-white px-3 py-1 rounded border border-slate-200 select-all">
                        {twoFASetupData.manual_entry_key}
                      </code>
                    </div>
                  </div>
                  
                  <form onSubmit={handleVerify2FASetup} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Enter the 6-digit code from your app
                      </label>
                      <input
                        type="text"
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg text-center text-2xl font-mono tracking-widest focus:ring-2 focus:ring-gold/50 focus:border-gold"
                        placeholder="000000"
                        maxLength={6}
                        data-testid="2fa-setup-code-input"
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={handleCancel2FASetup}
                        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={twoFALoading || twoFACode.length < 6}
                        className="flex-1 px-4 py-2 bg-gold text-navy font-medium rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="2fa-setup-verify-btn"
                      >
                        {twoFALoading ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          <>
                            <ShieldCheck size={16} />
                            Enable 2FA
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              ) : !twoFALoading && twoFAStatus && (
                <div className="space-y-4">
                  {/* 2FA Status Display */}
                  <div className={`flex items-center justify-between p-4 rounded-lg ${
                    twoFAStatus.enabled ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      {twoFAStatus.enabled ? (
                        <ShieldCheck className="w-6 h-6 text-green-600" />
                      ) : (
                        <ShieldOff className="w-6 h-6 text-slate-400" />
                      )}
                      <div>
                        <p className={`font-medium ${twoFAStatus.enabled ? 'text-green-800' : 'text-slate-700'}`}>
                          {twoFAStatus.enabled ? '2FA is enabled' : '2FA is not enabled'}
                        </p>
                        {twoFAStatus.enabled && twoFAStatus.setup_at && (
                          <p className="text-sm text-green-600">
                            Enabled on {new Date(twoFAStatus.setup_at).toLocaleDateString('en-GB')}
                          </p>
                        )}
                        {twoFAStatus.enabled && (
                          <p className="text-sm text-green-600">
                            {twoFAStatus.backup_codes_remaining} backup codes remaining
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {!twoFAStatus.enabled ? (
                      <button
                        onClick={handleStart2FASetup}
                        disabled={twoFALoading}
                        className="flex items-center gap-2 px-4 py-2 bg-gold text-navy font-medium rounded-lg hover:bg-gold/90 transition-colors"
                        data-testid="2fa-enable-btn"
                      >
                        <ShieldCheck size={16} />
                        Enable 2FA
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowRegenerateBackup(true)}
                          className="flex items-center gap-2 px-3 py-2 text-navy bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm"
                          data-testid="2fa-regenerate-btn"
                        >
                          <RefreshCw size={14} />
                          New Codes
                        </button>
                        <button
                          onClick={() => setShowDisable2FA(true)}
                          className="flex items-center gap-2 px-3 py-2 text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-sm"
                          data-testid="2fa-disable-btn"
                        >
                          <ShieldOff size={14} />
                          Disable
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Disable 2FA Form */}
                  {showDisable2FA && (
                    <form onSubmit={handleDisable2FA} className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <h4 className="font-medium text-red-800 mb-2">Disable Two-Factor Authentication</h4>
                      <p className="text-sm text-red-600 mb-3">
                        Enter a verification code to confirm. This will remove 2FA protection from your account.
                      </p>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={disableCode}
                          onChange={(e) => setDisableCode(e.target.value)}
                          className="flex-1 px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500/50"
                          placeholder="Enter code"
                          data-testid="2fa-disable-code-input"
                        />
                        <button
                          type="button"
                          onClick={() => { setShowDisable2FA(false); setDisableCode(''); setTwoFAError(''); }}
                          className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-white transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={twoFALoading || !disableCode}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                          data-testid="2fa-disable-confirm-btn"
                        >
                          {twoFALoading ? <Loader2 size={16} className="animate-spin" /> : 'Disable'}
                        </button>
                      </div>
                    </form>
                  )}
                  
                  {/* Regenerate Backup Codes Form */}
                  {showRegenerateBackup && (
                    <form onSubmit={handleRegenerateBackupCodes} className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <h4 className="font-medium text-amber-800 mb-2">Regenerate Backup Codes</h4>
                      <p className="text-sm text-amber-600 mb-3">
                        Enter a verification code to generate new backup codes. All existing codes will be invalidated.
                      </p>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={regenerateCode}
                          onChange={(e) => setRegenerateCode(e.target.value)}
                          className="flex-1 px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500/50"
                          placeholder="Enter code"
                          data-testid="2fa-regenerate-code-input"
                        />
                        <button
                          type="button"
                          onClick={() => { setShowRegenerateBackup(false); setRegenerateCode(''); setTwoFAError(''); }}
                          className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-white transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={twoFALoading || !regenerateCode}
                          className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                          data-testid="2fa-regenerate-confirm-btn"
                        >
                          {twoFALoading ? <Loader2 size={16} className="animate-spin" /> : 'Generate'}
                        </button>
                      </div>
                    </form>
                  )}
                  
                  {/* 2FA Info */}
                  {!twoFAStatus.enabled && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-800 mb-2">Why enable 2FA?</h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Protects your account even if your password is compromised</li>
                        <li>• Required for high-security admin operations</li>
                        <li>• Works with Google Authenticator, Authy, and other TOTP apps</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Timezone Settings */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-navy mb-4">Timezone Settings</h2>
              <p className="text-sm text-slate-500 mb-3">Configure default timezone for publishing</p>
              <select className="w-full max-w-md px-4 py-2 border border-slate-300 rounded-lg">
                <option value="Europe/London">Europe/London (GMT/BST)</option>
                <option value="America/New_York">America/New York (EST/EDT)</option>
                <option value="America/Los_Angeles">America/Los Angeles (PST/PDT)</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-navy mb-4">Invite New Admin</h3>
            
            <form onSubmit={handleCreateInvite} className="space-y-4">
              {inviteError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle size={16} />
                  {inviteError}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-gold/50 focus:border-gold"
                  required
                  data-testid="invite-email-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={inviteForm.first_name}
                    onChange={(e) => setInviteForm({...inviteForm, first_name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-gold/50 focus:border-gold"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={inviteForm.last_name}
                    onChange={(e) => setInviteForm({...inviteForm, last_name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-gold/50 focus:border-gold"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({...inviteForm, role: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-gold/50 focus:border-gold"
                  data-testid="invite-role-select"
                >
                  <option value="EDITOR">Editor</option>
                  <option value="ADMIN">Admin</option>
                  <option value="SUPER_ADMIN">Super Admin</option>
                </select>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteError('');
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteLoading}
                  className="flex-1 px-4 py-2 bg-gold text-navy font-medium rounded-lg hover:bg-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="invite-submit-btn"
                >
                  {inviteLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Invite'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
